import type { PointListe } from '@/lib/controle';
import { cn } from '@/lib/utils';
import { Icon } from '@pms-digital/design-system';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { styleStatut } from './mention-statut';

interface CompteurPointsProps {
  /** The status of Tahiti: its code gives the icon and the colour, its label is Tahiti's. */
  statut: { code: string; libelle: string };
  /** The number of Tahiti (`compte_statuts`). */
  nombre: number;
  /** The points of this status that have a target in the screen; none: the counter is inactive. */
  points: readonly PointListe[];
  /** A point was chosen: the screen goes to it. */
  onChoisir: (point: PointListe) => void;
}

interface Position {
  top: number;
  left: number;
}

/**
 * What a counter does under the pointer, when pressed and while its list is open (#30): the same
 * for the three statuses, whatever their colour. `gray-100` behind it (the hover of the history,
 * #17), a darker frame while it is pressed. The text of every status is above 4.5:1 on that grey
 * ("À relire" is `jaune-deeper`, not `jaune-deep`: 3.9:1 there, which is why this used to be a
 * special case with a yellow frame and an underline). The focus is the global rule.
 */
const RETOUR = 'hover:bg-gray-100 aria-expanded:bg-gray-100 active:border-gray-500';

const MARGE_PX = 8;
const ECART_PX = 4;

/**
 * A counter of the summary of the control ("Bloquant 1", "À relire 1", "OK 10"), as a button that
 * opens the small list of its points (#25). Its colour, icon and label are Tahiti's status's. A
 * counter with no point to list is `aria-disabled` and opens nothing (no empty list).
 *
 * The list is a menu (`role="menu"`, `menuitem`), `fixed` in a portal so the edge of the page
 * never cuts it. `Entrée`, `Espace` and `Flèche bas` open it on its first point (`Flèche haut` on
 * its last); the arrows, `Home` and `End` move; `Échap` closes it and gives the focus back to the
 * counter; `Tab` and a click outside close it.
 */
export function CompteurPoints({ statut, nombre, points, onChoisir }: CompteurPointsProps) {
  const idMenu = useId();
  const bouton = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [ouvert, setOuvert] = useState(false);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });
  // The point the focus goes to when the menu opens: the first, or the last (`Flèche haut`).
  const depart = useRef<'premier' | 'dernier'>('premier');
  const { icone, couleur } = styleStatut(statut.code);
  const actif = points.length > 0;

  const placer = useCallback(() => {
    const cible = bouton.current;
    const element = menu.current;
    if (cible === null || element === null) return;
    const rect = cible.getBoundingClientRect();
    const { width, height } = element.getBoundingClientRect();
    const left = Math.max(MARGE_PX, Math.min(rect.left, window.innerWidth - width - MARGE_PX));
    const dessous = rect.bottom + ECART_PX;
    const top =
      dessous + height + MARGE_PX <= window.innerHeight
        ? dessous
        : Math.max(MARGE_PX, rect.top - ECART_PX - height);
    setPosition({ top, left });
  }, []);

  const items = (): HTMLElement[] =>
    menu.current === null
      ? []
      : [...menu.current.querySelectorAll<HTMLElement>('[role="menuitem"]')];

  const fermer = useCallback((rendreLeFocus: boolean) => {
    setOuvert(false);
    if (rendreLeFocus) bouton.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!ouvert) return;
    placer();
    const liste = items();
    (depart.current === 'dernier' ? liste[liste.length - 1] : liste[0])?.focus();
  }, [ouvert, placer]);

  useEffect(() => {
    if (!ouvert) return;
    function dehors(event: PointerEvent) {
      const cible = event.target as Node;
      if (menu.current?.contains(cible) || bouton.current?.contains(cible)) return;
      setOuvert(false);
    }
    document.addEventListener('pointerdown', dehors);
    window.addEventListener('resize', placer);
    window.addEventListener('scroll', placer, true);
    return () => {
      document.removeEventListener('pointerdown', dehors);
      window.removeEventListener('resize', placer);
      window.removeEventListener('scroll', placer, true);
    };
  }, [ouvert, placer]);

  function ouvrir(par: 'premier' | 'dernier') {
    depart.current = par;
    setOuvert(true);
  }

  function surBouton(event: KeyboardEvent<HTMLButtonElement>) {
    if (!actif) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      ouvrir(event.key === 'ArrowUp' ? 'dernier' : 'premier');
    }
  }

  function surMenu(event: KeyboardEvent<HTMLDivElement>) {
    const liste = items();
    const rang = liste.indexOf(document.activeElement as HTMLElement);
    const aller = (cible: number) => {
      event.preventDefault();
      liste[(cible + liste.length) % liste.length]?.focus();
    };
    switch (event.key) {
      case 'ArrowDown':
        aller(rang + 1);
        break;
      case 'ArrowUp':
        aller(rang - 1);
        break;
      case 'Home':
        aller(0);
        break;
      case 'End':
        aller(liste.length - 1);
        break;
      case 'Escape':
        event.preventDefault();
        fermer(true);
        break;
      case 'Tab':
        // The focus goes back to the counter, and the Tab goes on from there.
        fermer(true);
        break;
    }
  }

  return (
    <>
      <button
        ref={bouton}
        type="button"
        aria-haspopup={actif ? 'menu' : undefined}
        aria-expanded={actif ? ouvert : undefined}
        aria-controls={actif && ouvert ? idMenu : undefined}
        aria-disabled={actif ? undefined : true}
        onClick={() => {
          if (!actif) return;
          if (ouvert) fermer(false);
          else ouvrir('premier');
        }}
        onKeyDown={surBouton}
        className={cn(
          'inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-md border border-gray-200 px-3 font-semibold text-small',
          actif && RETOUR,
          couleur,
        )}
      >
        <Icon name={icone} size={20} />
        {statut.libelle}
        <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-gray-50 px-2 font-bold text-caption text-black">
          {nombre}
        </span>
      </button>
      {ouvert && actif
        ? createPortal(
            <div
              ref={menu}
              id={idMenu}
              role="menu"
              aria-label={`Points ${statut.libelle}`}
              onKeyDown={surMenu}
              style={{ top: position.top, left: position.left }}
              className="fixed z-50 min-w-[220px] max-w-[calc(100vw-16px)] rounded-md border border-gray-300 bg-white py-1 shadow-md"
            >
              {points.map((point) => (
                <button
                  key={point.numero}
                  type="button"
                  role="menuitem"
                  tabIndex={-1}
                  onClick={() => {
                    fermer(false);
                    onChoisir(point);
                  }}
                  className="block h-9 w-full px-4 text-left font-normal text-black text-small hover:bg-gray-100 focus-visible:outline-offset-[-3px]"
                >
                  {point.nom}
                </button>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
